import { PriceZoneSystem } from '@schuelerhilfe/shared/src/enums/priceZoneSystem';
import { findActivePriceZone } from '@schuelerhilfe/shared/src/pricing/priceZones';
import type { LocationDto } from '@/lib/api';
import {
  usableOfficeHours,
  type OfficeHourEntry,
} from '@/lib/location/officeHours';

// Schema.org uses 2-letter day abbreviations (always English, not locale-dependent)
const WEEKDAY_JSONLD: Record<string, string> = {
  MONDAY: 'Mo',
  TUESDAY: 'Tu',
  WEDNESDAY: 'We',
  THURSDAY: 'Th',
  FRIDAY: 'Fr',
  SATURDAY: 'Sa',
  SUNDAY: 'Su',
};

export interface DisplayHour {
  day: string;
  hours: string;
}

/**
 * Translation function shape — accepts a key and optional params.
 * Matches the return type of `useTranslations('schoolDetails')`.
 */
type TranslateFn = (key: string, params?: Record<string, string>) => string;

/**
 * Converts OfficeHourDTO[] to display rows using i18n translations.
 * Filters to standard=true, disabled!=true entries.
 *
 * Translation keys used: `weekday.MONDAY`..`weekday.SUNDAY`, `closed`, `hoursFormat`
 */
export function mapOfficeHours(
  officeHours: OfficeHourEntry[] | undefined,
  t: TranslateFn,
): DisplayHour[] {
  return usableOfficeHours(officeHours).map((h) => ({
    day: h.weekday ? t(`weekday.${h.weekday}`) : '',
    hours: h.closed
      ? t('closed')
      : t('hoursFormat', {
          start: h.startTime ?? '',
          end: h.endTime ?? '',
        }),
  }));
}

// The two tariffs Schülerhilfe defines as the advertised range (SH-466), in
// the order they appear: `von {price_24_3} bis {price_12_1}`.
const PRICE_RANGE_FROM = 'price_24_3';
const PRICE_RANGE_TO = 'price_12_1';

/**
 * Formats the JSON-LD `priceRange` string from the active price zone's
 * `price_24_3` and `price_12_1` entries. Placeholders are matched with or
 * without their surrounding brackets.
 * Returns null unless both prices are present.
 */
export function formatPriceRange(
  priceZones: LocationDto['priceZones'],
  system: PriceZoneSystem,
): string | null {
  const zone = findActivePriceZone(priceZones, system);
  if (!zone) {
    return null;
  }

  const priceOf = (placeholder: string) => {
    for (const product of zone.products ?? []) {
      for (const price of product.prices ?? []) {
        if (
          price.placeholder === placeholder ||
          price.placeholder === `[${placeholder}]`
        ) {
          return price.price;
        }
      }
    }
    return undefined;
  };

  const from = priceOf(PRICE_RANGE_FROM);
  const to = priceOf(PRICE_RANGE_TO);
  if (typeof from !== 'number' || typeof to !== 'number') {
    return null;
  }

  const fmt = (v: number) =>
    v.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return `von ${fmt(from)}€ bis ${fmt(to)}€/h`;
}

/**
 * Builds Schema.org openingHours free-text strings.
 * Groups consecutive days with the same hours.
 * Uses English 2-letter abbreviations (Schema.org standard, not locale-dependent).
 */
export function buildOpeningHoursJsonLd(
  officeHours: OfficeHourEntry[] | undefined,
): string[] {
  const standard = usableOfficeHours(officeHours).filter((h) => !h.closed);

  if (standard.length === 0) {
    return [];
  }

  const groups: { days: string[]; hours: string }[] = [];
  for (const h of standard) {
    const hours = `${h.startTime ?? ''}-${h.endTime ?? ''}`;
    const day = WEEKDAY_JSONLD[h.weekday ?? ''] ?? '';
    const last = groups[groups.length - 1];
    if (last && last.hours === hours) {
      last.days.push(day);
    } else {
      groups.push({ days: [day], hours });
    }
  }

  return groups.map((g) => `${g.days.join(',')} ${g.hours}`);
}

/**
 * Tidies a plain-text block an editor or partner typed into a textarea so it can
 * be rendered with `whitespace-pre-line`: line breaks survive, but a run of
 * blank lines collapses to a single one (SH-344 item 2.11).
 *
 * Without the collapse, Enter is a layout tool — one partner could push the rest
 * of their Standort page down an arbitrary distance. A blank line that holds
 * only spaces or tabs counts as blank, because a textarea makes those by
 * accident and nothing in the rendering tells them apart.
 */
export function collapseBlankLines(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/[^\S\n]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
